import React from "react";
import Link from "next/link";

// Markdown pieces shared by the article and its richer blocks: anchor ids,
// links and the element styles MDX renders with.

// Heading text to an anchor id. The rail and the h2s both go through this,
// so a link always finds its heading.
export const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[*_`]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const textOf = (node: React.ReactNode): string =>
  typeof node === "string" || typeof node === "number"
    ? String(node)
    : Array.isArray(node)
      ? node.map(textOf).join("")
      : React.isValidElement<{ children?: React.ReactNode }>(node)
        ? textOf(node.props.children)
        : "";

// Internal links stay client-side; outside links open in a new tab.
export const A = ({ href = "", children }: React.ComponentProps<"a">) => {
  const cls =
    "tw-font-extrabold tw-text-jam-purple tw-underline tw-decoration-jam-soft tw-decoration-2 tw-underline-offset-[3px] hover:tw-decoration-jam-purple";
  return href.startsWith("/") ? (
    <Link href={href} className={cls}>
      {children}
    </Link>
  ) : (
    <a href={href} target="_blank" rel="noopener" className={cls}>
      {children}
    </a>
  );
};

export const H2 = ({ children }: { children?: React.ReactNode }) => (
  <h2
    id={slugify(textOf(children))}
    className="jam-display tw-mb-4 tw-mt-12 tw-scroll-mt-24 tw-text-[28px] tw-font-extrabold tw-leading-[1.1] tw-text-jam-ink first:tw-mt-0 md:tw-text-[34px]"
  >
    {children}
  </h2>
);

export const MD_COMPONENTS = {
  a: A,
  h2: ({ children }: React.ComponentProps<"h2">) => <H2>{children}</H2>,
  h3: (p: React.ComponentProps<"h3">) => (
    <h3
      className="jam-display tw-mb-2 tw-mt-7 tw-text-xl tw-font-extrabold tw-text-jam-ink"
      {...p}
    />
  ),
  p: (p: React.ComponentProps<"p">) => (
    <p className="tw-my-4 tw-leading-[1.75]" {...p} />
  ),
  ul: (p: React.ComponentProps<"ul">) => (
    <ul
      className="tw-my-4 tw-flex tw-list-none tw-flex-col tw-gap-3 tw-p-0"
      {...p}
    />
  ),
  ol: (p: React.ComponentProps<"ol">) => (
    <ol
      className="tw-my-4 tw-flex tw-list-decimal tw-flex-col tw-gap-3 tw-pl-6"
      {...p}
    />
  ),
  li: ({ children }: React.ComponentProps<"li">) => (
    <li className="tw-relative tw-pl-7 tw-leading-[1.7] [ol_&]:tw-pl-1">
      <span
        aria-hidden="true"
        className="tw-absolute tw-left-0 tw-top-[9px] tw-h-3 tw-w-3 tw-rounded-full tw-border-2 tw-border-solid tw-border-jam-ink tw-bg-jam-lime [ol_&]:tw-hidden"
      />
      {children}
    </li>
  ),
  strong: (p: React.ComponentProps<"strong">) => (
    <strong className="tw-font-extrabold tw-text-jam-ink" {...p} />
  ),
};

// Step bodies sit inside a card, so no outer paragraph margins.
export const STEP_COMPONENTS = {
  ...MD_COMPONENTS,
  p: (p: React.ComponentProps<"p">) => (
    <p className="tw-m-0 tw-leading-[1.7]" {...p} />
  ),
};
